// Polling lifecycle composable (Story 2.14).
//
// Cada hero panel envuelve su fetch con `usePoll(fetchFn, opts)`. La
// composable se encarga de:
//   - Inmediato + cada `intervalMs` (30 s default per UX-DR41).
//   - Pausa cuando `document.visibilityState === 'hidden'`.
//   - Pausa cuando el usuario está idle por `idleMs` (5 min default).
//     Idle detection: mousemove + keydown + touchstart resetean.
//   - AbortController per fetch, registrado en `useTenantStore` para
//     que `setActiveTenant` y `setTimeWindow` puedan abort-all.
//   - Last-known-good: un fallo inicial muestra error; tras un éxito,
//     los fallos conservan los datos y exponen estado degradado.
//
// El composable NO posee el state — el caller (panel component) usa
// `usePanelState` de Story 2.13 y le pasa los handlers via opts.

import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';

import { config } from '@/config.js';
import { useTenantStore } from '@/stores/tenant.js';

const IDLE_EVENTS = ['mousemove', 'keydown', 'touchstart'];

export function usePoll(fetchFn, opts = {}) {
  const intervalMs = opts.intervalMs ?? config.pollIntervalMs;
  const idleMs = opts.idleMs ?? config.idlePauseMs;
  const onSuccess = opts.onSuccess;
  const onError = opts.onError;
  const onDegraded = opts.onDegraded;
  const watchSources = opts.watch ?? [];

  const lastUpdatedAt = ref(null);
  const status = reactive({
    lastAttemptAt: null,
    refreshing: false,
    degraded: false,
    failureCount: 0,
    contextResetting: false,
    stale: false,
    paused: false
  });
  const stale = ref(false);
  const tenant = useTenantStore();

  let intervalHandle = null;
  let idleTimerHandle = null;
  let staleTimerHandle = null;
  let isPaused = false;
  let currentController = null;
  let failureCount = 0;
  let unmounted = false;

  function clearIdle() {
    if (idleTimerHandle) { clearTimeout(idleTimerHandle); idleTimerHandle = null; }
  }
  function clearStaleTimer() {
    if (staleTimerHandle) { clearTimeout(staleTimerHandle); staleTimerHandle = null; }
  }
  function scheduleStale() {
    clearStaleTimer();
    staleTimerHandle = setTimeout(() => {
      if (!lastUpdatedAt.value) return;
      stale.value = true;
      status.stale = true;
    }, intervalMs * 2);
  }
  function startIdleTimer() {
    clearIdle();
    idleTimerHandle = setTimeout(() => {
      if (unmounted) return;
      if (!isPaused) {
        isPaused = true;
        status.paused = true;
        stopInterval();
        clearStaleTimer();
      }
    }, idleMs);
  }
  function onActivity() {
    if (unmounted) return;
    if (isPaused && document.visibilityState !== 'hidden') {
      isPaused = false;
      status.paused = false;
      // Resume: fetch immediato + reanudar interval.
      tick();
      startInterval();
    }
    startIdleTimer();
  }

  function startInterval() {
    if (intervalHandle) return;
    intervalHandle = setInterval(() => { tick(); }, intervalMs);
  }
  function stopInterval() {
    if (intervalHandle) { clearInterval(intervalHandle); intervalHandle = null; }
  }

  function resetStatus() {
    lastUpdatedAt.value = null;
    failureCount = 0;
    status.lastAttemptAt = null;
    status.refreshing = false;
    status.degraded = false;
    status.failureCount = 0;
    status.contextResetting = true;
    stale.value = false;
    status.stale = false;
    clearStaleTimer();
  }

  async function tick() {
    if (unmounted || document.visibilityState === 'hidden') return;
    if (isPaused) {
      isPaused = false;
      status.paused = false;
      startInterval();
      startIdleTimer();
    }
    // Los polls/retries repetidos comparten el intento activo. Un cambio
    // de contexto ya lo aborta desde el store antes de disparar el watcher.
    if (currentController && !currentController.signal.aborted) return;
    const controller = new AbortController();
    currentController = controller;
    status.refreshing = true;
    status.lastAttemptAt = new Date();
    tenant.registerAbortController?.(controller);
    try {
      const result = await fetchFn({ signal: controller.signal });
      if (controller.signal.aborted) return;
      const validResult = result !== undefined
        && result !== null
        && (typeof result !== 'object' || !('ok' in result) || result.ok === true);
      if (!validResult) throw new Error('Invalid polling response');
      if (typeof onSuccess === 'function' && onSuccess(result) === false) {
        throw new Error('Polling response was not accepted');
      }
      failureCount = 0;
      status.failureCount = 0;
      status.degraded = false;
      status.contextResetting = false;
      stale.value = false;
      status.stale = false;
      lastUpdatedAt.value = new Date();
      scheduleStale();
    } catch (err) {
      // Aborts no cuentan como failure.
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      failureCount += 1;
      status.failureCount = failureCount;
      status.degraded = Boolean(lastUpdatedAt.value);
      status.contextResetting = false;
      stale.value = !!lastUpdatedAt.value
        && Date.now() - lastUpdatedAt.value.getTime() >= intervalMs * 2;
      status.stale = stale.value;
      if (status.degraded && typeof onDegraded === 'function') onDegraded(err, status);
      if (!lastUpdatedAt.value && typeof onError === 'function') {
        onError(err);
      }
    } finally {
      tenant.deregisterAbortController?.(controller);
      if (currentController === controller) {
        currentController = null;
        status.refreshing = false;
      }
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      isPaused = true;
      status.paused = true;
      stopInterval();
      clearStaleTimer();
      if (currentController) {
        try { currentController.abort('hidden'); } catch { /* noop */ }
      }
    } else {
      isPaused = false;
      status.paused = false;
      tick();
      startInterval();
      startIdleTimer();
    }
  }

  // Re-fetch on changeKey bump (tenant or window change).
  const stopWatchers = [];
  if (watchSources.length > 0) {
    const stop = watch(watchSources, () => {
      resetStatus();
      if (typeof opts.onContextReset === 'function') opts.onContextReset();
      tick();
    });
    stopWatchers.push(stop);
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    for (const evt of IDLE_EVENTS) document.addEventListener(evt, onActivity, { passive: true });
    startIdleTimer();
    startInterval();
    tick(); // immediate fire
  });

  onBeforeUnmount(() => {
    unmounted = true;
    stopInterval();
    clearIdle();
    clearStaleTimer();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    for (const evt of IDLE_EVENTS) document.removeEventListener(evt, onActivity);
    if (currentController) {
      try { currentController.abort('unmount'); } catch { /* noop */ }
    }
    for (const stop of stopWatchers) stop();
  });

  async function retry() {
    if (document.visibilityState !== 'hidden') {
      isPaused = false;
      status.paused = false;
      startInterval();
      startIdleTimer();
    }
    return tick();
  }

  return { lastUpdatedAt, status, stale, tick, retry, resetStatus };
}
